import { Component, OnDestroy, OnInit, Output, ViewChild } from "@angular/core";
import {
  UserService,
  ProductService,
  ContentService,
  EventsService,
  EventBusService,
  OrderService,
  WindowService,
  StorageService,
  StorageKeys,
  environment,
  CheckoutService,
  HelperService,
} from "@mypxplat/xplat/core";
import { filter, take, takeUntil } from "rxjs/operators";
import { ActivatedRoute, Router } from "@angular/router";
import { AppService, AvoService, BetaService, FirebaseService } from "@mypxplat/xplat/web/core";
import { SphereHomeBaseComponent } from "@mypxplat/xplat/features";
import { NgxSpinnerService } from "ngx-spinner";
import { AngularFirestore } from "@angular/fire/compat/firestore";
import { AngularFireAuth } from "@angular/fire/compat/auth";
import {
  RecentOrderComponent,
  RegisterProductComponent,
  TermsofuseComponent,
  RegisterSuccessSplashComponent,
  StudiooneGracePeriodComponent,
  MissedPaymentModalComponent,
  AuthenticateFenderUserComponent,
  LinkFenderAccountComponent,
} from "../modals";
import { TranslateService } from "@ngx-translate/core";
import moment from "moment";
import { css, cx } from "styled-system/css";
import { bodyTextRecipe } from "@fenderdigital/ui-styled-system/recipes"; // using a recipe from the styled system package
import { buttonRecipe } from "@fenderdigital/ui-styled-system/recipes";
import { fetchAuthSession } from "aws-amplify/auth";

@Component({
  selector: "myp-dashboard-home",
  templateUrl: "dashboard-home.component.html",
  standalone: false,
})
export class DashboardHomeComponent extends SphereHomeBaseComponent implements OnInit, OnDestroy {
  // Panda CSS class for section titles
  sectionTitleClass = css({
    fontSize: "2xl",
    fontWeight: "bold",
    color: "blue.600",
    marginBottom: "4",
    textTransform: "uppercase",
    letterSpacing: "wide",
    borderBottom: "2px solid",
    borderColor: "blue.200",
    paddingBottom: "2",
  });

  // Expose bodyTextRecipe for template use
  bodyTextRecipe = bodyTextRecipe;
  buttonRecipe = buttonRecipe;
  cx = cx;
  css = css;

  public loading: boolean = true;
  public displayedSection: string = "News & Updates";
  public needsMoreData: boolean;
  public selectedVimeoID: string;
  public gettingS1Demo: boolean;
  public gettingNotionDemo: boolean;
  public hasS1App: any = false;
  public shouldSeeS1Demo: any = false;
  public hasFree: any = false;
  public hasProDemo: any = false;
  public hasNotion: any = false;
  public hasNotionDemo: any = false;
  public daysLeft: any = false;
  public redeemingMB: boolean = false;
  public termsResult: any = {};

  constructor(
    userService: UserService,
    private _router: Router,
    public appService: AppService,
    public productService: ProductService,
    public contentService: ContentService,
    public eventsService: EventsService,
    public fbAuth: AngularFireAuth,
    public db: AngularFirestore,
    private spinner: NgxSpinnerService,
    public helperService: HelperService,
    private _translate: TranslateService,
    public eventBusService: EventBusService,
    public orderService: OrderService,
    public windowService: WindowService,
    public avoService: AvoService,
    public storage: StorageService,
    public betaService: BetaService,
    public fbService: FirebaseService,
    public checkoutService: CheckoutService,
    public activatedRoute: ActivatedRoute
  ) {
    super(userService, productService, contentService, eventsService);
  }
  ngOnInit(): void {
    super.ngOnInit();
    this.appService.title = this.user.active_subscription ? this.env.studioOnePlusBrandName + ": Home" : "Fender: Home";
    if (!this.user) this.spinner.show();
    fetchAuthSession().then((session) => {
      if (session?.tokens?.idToken?.payload?.["custom:paeid"]) {
        // logged in with presonus password
        if (this.userService.user.fender_cognito_user_id) {
          // is linked, so make them unify passwords.
          this.appService.showModal(LinkFenderAccountComponent, { size: "lg" });
        }
      }
    });
    if (!this.userService.gettingUserDetails) {
      this.userService.getUserDetails(false).subscribe({
        next: (result) => {
          this.spinner.hide();
          if (result.subscription?.status == "canceled" && result.subscription?.cancel_reason == "terminate bluesnap" && !result.subscription?.schedule_subscription_id) {
            if (this._router.url != "/account/plan") {
              const modal = this.appService.showModal(MissedPaymentModalComponent, { size: "lg" });
              modal.componentInstance.type = "terminate bluesnap";
            }
          } else if (result.subscription?.missed_payment) {
            if (this._router.url != "/account/plan") {
              const modal = this.appService.showModal(MissedPaymentModalComponent, { size: "lg" });
              modal.componentInstance.missedPaymentDetails = result.subscription.missed_payment;
            }
          }
        },
        error: (err) => {
          this.logout();
        },
      });
    } else {
      this.userService.userDataRefreshed$.pipe(take(1)).subscribe((result) => {
        this.spinner.hide();
      });
    }

    this.contentService.educationDataStream$.pipe(takeUntil(this.destroy$)).subscribe((result) => {
      if (result) {
        this.videos = [];
        this.videosLoading = false;
        let availableVideos = result.videos.sort((a, b) => (a.created > b.created ? -1 : 1));
        if ((this.appService.videoContentType.fender && this.appService.videoContentType.presonus) || (!this.appService.videoContentType.fender && !this.appService.videoContentType.presonus)) {
          // both.
        } else {
          const selectedBrand = this.appService.videoContentType.fender ? "fender" : "presonus";
          availableVideos = availableVideos.filter((item) => item.brand == selectedBrand || item.brand == "both");
        }
        this.videos = availableVideos.filter((item, index) => index <= 3);
      }
    });
    if (this.activatedRoute.snapshot.queryParams.showLinkedAccountMessage) {
      const modal = this.appService.showModal(AuthenticateFenderUserComponent, { size: "lg" });
      modal.componentInstance.newFenderUserCreated = true;
      // then remove the query param so if you refresh your page you dont constantly see the modal.
      this._router.navigate(["."], { relativeTo: this.activatedRoute, queryParams: {} });
    }

    if (this.activatedRoute.snapshot.queryParams.register) {
      let splashId = this.activatedRoute.snapshot.queryParams.register;
      if (splashId && splashId == "mustangmicro") {
        this.registerProduct(splashId);
      } else {
        this.fbService.checkForSplash(undefined, splashId).then((result) => {
          this.registerProduct(splashId, result);
        });
      }
      // this removes the query param so if you refresh your page you dont constantly see the modal.
      this._router.navigate(["."], { relativeTo: this.activatedRoute, queryParams: {} });
    }
    if (this.activatedRoute.snapshot.queryParams.showSplash) {
      this._router.navigate([], {
        queryParams: { showSplash: null, splashProductId: null, splashProductName: null },
        queryParamsHandling: "merge",
      });
      this.fbService
        .handleFirestorePromise(() => this.fbService.doc("product_splashes/" + this.activatedRoute.snapshot.queryParams.showSplash).ref.get())
        .then((result) => {
          if (result.exists) {
            let splash: any = result.data();
            this.showSplash(splash, this.activatedRoute.snapshot.queryParams.splashProductId, this.activatedRoute.snapshot.queryParams.splashProductName);
          }
        })
        .catch(() => {});
    }

    if (!this.storage.checkTimestamp("logged_in_event_triggered")) {
      this.avoService.avoInitted$.pipe(filter((result) => !!result)).subscribe((result) => {
        this.avoService.trackEvent().loggedIn({
          userId_: this.user.id,
          prsId: this.user.id,
          siteId: this.userService.user?.active_subscription ? "studio_one_plus" : "mypresonus",
        });
        this.storage.setTimestamp("logged_in_event_triggered", 24);
      });
    }
    this.retrieveUpgradableProducts();
    this.productService.redeemS17ElligibilityRetrieved$
      .pipe(
        filter((result) => !!result),
        takeUntil(this.destroy$)
      )
      .subscribe((result) => {
        this.productService.products$
          .pipe(
            filter((products) => !!products),
            takeUntil(this.destroy$)
          )
          .subscribe((result) => {
            if (result) {
              this.productsLoading = false;
              this.recentProducts = this.productService.productsByAcquired.filter((item, index) => index < (this.productService.elligleToRedeemStudioOneSeven ? 2 : 3));
              this.setupDemoData();
            }
          });
      });
    this.checkDataWeNeed();
    if (!this.storage.checkTimestamp("checked_terms")) {
      this.userService.checkTerms().subscribe((result) => {
        this.storage.setTimestamp("checked_terms", 168);
        this.termsResult = result;
      });
    }
  }

  retrieveUpgradableProducts() {
    if (!this.checkoutService.upgradableStudioOneProducts) {
      this.checkoutService.getUpgradableProducts().subscribe();
    }
  }

  tileClick(prod) {
    this._router.navigate(["products/detail/", prod.id]);
  }

  private getUrlParameter(url, name) {
    name = name.replace(/[\[]/, "\\[").replace(/[\]]/, "\\]");
    var regex = new RegExp("[\\?&]" + name + "=([^&#]*)");
    var results = regex.exec(url);
    return results === null ? "" : decodeURIComponent(results[1].replace(/\+/g, " "));
  }

  showTerms(html, showAccept = true) {
    const modalRef = this.appService.showModal(TermsofuseComponent, { size: "lg" });
    modalRef.componentInstance.termsHtml = html;
    modalRef.componentInstance.showAcceptButton = showAccept;
  }

  showVideo(video) {
    if (this.user.active_subscription) {
      let parts = video.uri.split("/");
      this.eventBusService.emit(this.eventBusService.types.playVideo, {
        id: parts[parts.length - 1],
        title: video.name,
        type: "vimeo",
        thumb: video.pictures.sizes[3].link,
      });
    } else {
      this.upgrade();
    }
  }

  openLivestream(id) {
    let video = this.contentService.videosByPlatformId[id];
    if (video) {
      this.eventBusService.emit(this.eventBusService.types.playVideo, {
        id: video.platform_id,
        title: video.title,
        type: video.platform,
        thumb: video.image,
        presonus_id: video.id,
        presonusVideo: video,
      });
    } else {
      this.eventBusService.emit(this.eventBusService.types.playVideo, {
        id: id,
        type: "vimeo",
      });
    }
  }

  public profileProgress: any = {
    percentage: 0,
    completed: 0,
    total: 0,
  };
  checkDataWeNeed() {
    let hasAll = true;
    this.userService.dataWeNeed.forEach((item) => {
      if (!this.user[item.key]) {
        hasAll = false;
      }
    });

    this.needsMoreData = !hasAll;
    let completed = (this.profileProgress.completed = this.userService.dataWeNeed.filter((item) => {
      return item.key == "skills" ? this.user.skills?.length : this.user[item.key];
    }).length);
    this.profileProgress.percentage = Math.round((completed / this.userService.dataWeNeed.length) * 100);
    this.profileProgress.completed = completed;
    this.profileProgress.total = this.userService.dataWeNeed.length;
  }

  openUrl(url) {
    window.open(url);
  }

  getFreeProduct(product) {
    if (product.indexOf("notion") > -1) {
      this.gettingNotionDemo = true;
    } else {
      this.gettingS1Demo = true;
    }
    this.productService.getFreeProduct(product, "mypresonus").subscribe((result: any) => {
      this.avoService.trackEvent().orderCompleted({
        prsId: this.user.id,
        subProvider: this.user.subscription?.provider || undefined,
        subStartDate: this.user.subscription?.start_date || undefined,
        subEndDate: this.user.subscription?.end_date || undefined,
        subType: this.user.subscription?.type || undefined,
        prsSubStatus: this.user.subscription?.status || undefined,
        locale: (this.user?.language_code || "en") + "-" + (this.user?.country || "US"),
        subtotal: 0,
        price: 0.0,
        quantity: 1,
        products: [
          {
            productId: this.gettingNotionDemo ? "395" : "1582",
            price: 0.0,
            sku: this.gettingNotionDemo ? "395" : "1582",
            name: this.gettingNotionDemo ? "Notion 6 Demo" : "Studio One 6 Demo",
          },
        ],
        userId_: this.user.id,
      });
      this.productService.getProducts(true).subscribe(() => {
        if (result.licenseId) {
          if (this.gettingS1Demo) {
            this.fbService.checkForSplash("studioapp7.demo").then((splash) => {
              this.showSplash(splash, environment.product_ids.s17demo, "Studio One 7 Demo", true);
              this._router.navigate(["products/detail", result.licenseId]);
            });
          } else {
            this._router.navigate(["products/detail", result.licenseId]);
          }
        }
        this.gettingS1Demo = false;
        this.gettingNotionDemo = false;
      });
    });
  }

  showS16Redeem() {
    const modalRef = this.appService.showModal(StudiooneGracePeriodComponent, {
      size: "lg",
      container: this.appService.appContainer,
    });
  }

  showSplash(splash, productId?, productName?, isNewRegistration?) {
    const modalRef = this.appService.showModal(RegisterSuccessSplashComponent, {
      size: "lg",
      container: this.appService.appContainer,
    });
    modalRef.componentInstance.splash = splash;
    if (productId) modalRef.componentInstance.productId = productId;
    if (productName) modalRef.componentInstance.productName = productName;
    if (isNewRegistration) modalRef.componentInstance.isNewRegistration = true;
  }

  setupDemoData() {
    this.hasS1App = this.productService.productsByErpIDs["STUDIOONE7"];
    this.shouldSeeS1Demo =
      !this.user.active_subscription && !this.productService.productsByStringIDs["studioapp7.demo"] && !this.productService.productsByStringIDs["studioapp7.pro"] && this.env.features.s17_demo;
    this.hasFree = this.productService.productsByStringIDs["studioapp7.free"];
    this.hasProDemo = this.productService.productsByStringIDs["studioapp7.demo"];
    this.hasNotion = this.productService.productsByStringIDs["notion6"];
    this.hasNotionDemo = this.productService.productsByStringIDs["notion6.demo"];
    if (this.hasProDemo) {
      if (this.hasProDemo.expirationDate) {
        const expires = moment(this.hasProDemo.expirationDate);
        const today = moment();
        this.daysLeft = today.diff(expires, "days") * -1;
        if (this.daysLeft < 0) {
          this.daysLeft = 0;
        } else {
          this.daysLeft = 30;
        }
      }
    }
  }

  upgrade() {
    this._router.navigate(["/onboarding"], { queryParams: { subscribeOnly: true } });
  }

  logout() {
    this._router.navigate(["/logout"]);
  }

  registerProduct(splashId?, splashData?) {
    let regInProcessString;
    this._translate.get("products.register.registration_in_process").subscribe((string) => {
      regInProcessString = string;
    });
    const modalRef = this.appService.showModal(RegisterProductComponent, {
      size: splashId == "mustangmicro" || splashData?.registerCustomUI ? "md" : "lg",
      ariaLabelledBy: "modal-title",
      container: this.appService.appContainer,
      beforeDismiss: () => {
        if (modalRef.componentInstance.registering) {
          alert(regInProcessString);
          return false;
        }
      },
    });
    // there is special handling for mustang micro registrations.
    if (splashId == "mustangmicro") {
      modalRef.componentInstance.mustang = true;
    } else if (splashData?.registerCustomUI) {
      modalRef.componentInstance.registerCustomUI = splashData.registerCustomUI;
    }
  }

  redeemS17() {
    const modalRef = this.appService.showModal(StudiooneGracePeriodComponent, {
      size: "lg",
      container: this.appService.appContainer,
    });
    modalRef.componentInstance.asset = this.productService.s16ProductToUpgradeTo7;
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.appService.fullscreenHeader = undefined;
  }
}
